import { useEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { api } from '../lib/api'
import { getRichTwitter, setRichTwitter } from '../lib/prefs'
import { loadTweet } from '../lib/twitterWidgets'
import { displayUrl } from '../lib/displayUrl'
import type { ChatLink, OGResponse, Overlay } from '../types'

interface Props {
  channelId: string
  link: ChatLink | null
}

export default function PreviewPanel({ channelId, link }: Props) {
  const [data, setData] = useState<OGResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!link) {
      setData(null)
      return
    }
    setLoading(true)
    setErr('')
    api<OGResponse>(`/api/channels/${channelId}/links/${link.id}/og`)
      .then(setData)
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false))
  }, [channelId, link?.id])

  if (!link) return <EmptyState />
  if (loading) return <div className="p-4 text-sm text-gray-500">Yükleniyor…</div>
  if (err) return <Fallback url={displayUrl(link)} message={err} />
  if (!data) return <Fallback url={displayUrl(link)} />

  switch (data.kind) {
    case 'youtube':
      return data.embed_url ? (
        <div className="p-4">
          {data.title && <h3 className="font-semibold mb-2">{data.title}</h3>}
          <iframe
            src={data.embed_url}
            className="w-full aspect-video rounded"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-2 inline-block">
            Yeni sekmede aç
          </a>
        </div>
      ) : (
        <Fallback url={data.url} />
      )

    case 'twitter':
      return (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            {data.title ? (
              <div className="text-xs text-[var(--color-text-muted)]">@{data.title}</div>
            ) : <span />}
            <RichTwitterToggle />
          </div>
          <TwitterEmbed html={data.html || ''} />
          <div className="mt-3 flex items-center gap-3">
            <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-xs underline text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
              X'te aç
            </a>
            <PromoteToOverlay channelId={channelId} tweetURL={data.url} />
          </div>
        </div>
      )

    case 'og':
    default:
      return (
        <div className="p-4">
          {data.image && (
            <img
              src={data.image}
              alt=""
              className="w-full h-48 object-cover rounded mb-3"
              loading="lazy"
            />
          )}
          {data.title && <h3 className="font-semibold mb-1">{data.title}</h3>}
          {data.description && <p className="text-sm text-gray-600 mb-3">{data.description}</p>}
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-3 py-1 bg-black text-white rounded text-sm"
          >
            Aç
          </a>
        </div>
      )
  }
}

// TwitterEmbed — sanitize edilmiş blockquote render eder. Rich preference açıksa
// platform.twitter.com/widgets.js yüklenip blockquote canlı tweet iframe'ine dönüşür
// (foto/video/like sayıları, threading). Kapalıysa sadece statik blockquote.
function TwitterEmbed({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [rich, setRich] = useState<boolean>(getRichTwitter())

  // Pref değişince re-render
  useEffect(() => {
    const handler = () => setRich(getRichTwitter())
    window.addEventListener('prefs:richTwitter', handler)
    return () => window.removeEventListener('prefs:richTwitter', handler)
  }, [])

  useEffect(() => {
    if (!ref.current) return
    const fragment = DOMPurify.sanitize(html, {
      ADD_TAGS: ['blockquote'],
      ADD_ATTR: ['lang', 'dir', 'class'],
      RETURN_DOM_FRAGMENT: true,
    }) as DocumentFragment
    ref.current.replaceChildren(fragment)

    if (rich) {
      // widgets.js yüklendikten sonra subtree'yi tweet iframe'ine dönüştür
      loadTweet(ref.current)
    }
  }, [html, rich])

  return <div ref={ref} className="twitter-embed" />
}

// RichTwitterToggle — Twitter preview'ında widgets.js'i aç/kapat.
function RichTwitterToggle() {
  const [rich, setRich] = useState<boolean>(getRichTwitter())
  return (
    <button
      onClick={() => {
        const next = !rich
        setRich(next)
        setRichTwitter(next)
      }}
      title={rich ? 'Sadece metin göster' : 'Canlı tweet (foto/video) göster'}
      className={[
        'text-xs px-2 py-1 rounded border transition-colors',
        rich
          ? 'bg-[var(--color-accent-soft)] border-[var(--color-accent)]/40 text-[var(--color-accent-hover)]'
          : 'bg-transparent border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-border-strong)]',
      ].join(' ')}
    >
      {rich ? '✦ Zengin' : 'Statik'}
    </button>
  )
}

// PromoteToOverlay — twitter link'i seçili kanalın twitter-feed overlay'lerinden birine push eder.
function PromoteToOverlay({ channelId, tweetURL }: { channelId: string; tweetURL: string }) {
  const [overlays, setOverlays] = useState<Overlay[]>([])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!open) return
    api<Overlay[]>(`/api/channels/${channelId}/overlays`)
      .then((all) => setOverlays(all.filter((o) => o.type === 'social' && o.variant === 'twitter-feed')))
      .catch(() => setOverlays([]))
  }, [open, channelId])

  async function send(overlayId: string) {
    setBusy(true)
    setMsg('')
    try {
      await api(`/api/overlays/${overlayId}/tweets`, {
        method: 'POST',
        body: JSON.stringify({ url: tweetURL }),
      })
      setMsg('✓ Eklendi')
      setTimeout(() => setOpen(false), 1500)
    } catch (e) {
      setMsg('Hata: ' + (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs px-2 py-1 bg-sky-100 text-sky-800 rounded hover:bg-sky-200"
      >
        🐦 Overlay'e gönder
      </button>
    )
  }

  return (
    <div className="border rounded p-2 bg-gray-50 text-xs">
      <div className="font-semibold mb-1">Hangi overlay'e?</div>
      {overlays.length === 0 && <div className="text-gray-500">Bu kanalda twitter-feed overlay yok.</div>}
      <ul className="space-y-1">
        {overlays.map((o) => (
          <li key={o.id}>
            <button
              disabled={busy}
              onClick={() => send(o.id)}
              className="text-left w-full hover:bg-white px-2 py-1 rounded disabled:opacity-50"
            >
              {o.name}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex justify-between items-center">
        <button onClick={() => setOpen(false)} className="text-gray-500">İptal</button>
        {msg && <span>{msg}</span>}
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
      Önizleme için bir link seçin
    </div>
  )
}

function Fallback({ url, message }: { url: string; message?: string }) {
  return (
    <div className="p-4 text-sm">
      <p className="text-gray-600 mb-2">{message || 'Önizleme alınamadı.'}</p>
      <a href={url} target="_blank" rel="noopener noreferrer" className="underline break-all">{url}</a>
    </div>
  )
}
